/**
 * How a support ticket's stored values are shown to the shop that raised it.
 *
 * Shared by the dashboard and the detail page so the two cannot drift, and separate from both
 * so it can be tested without rendering either.
 *
 * The wording is the customer's, not HQ's. `new` and `open` are one thing from this side —
 * nobody outside the desk cares whether a request has been triaged yet, only whether anyone
 * is looking at it — and `idea` is offered as "Suggestion" because that is the word on the
 * form, while the stored value matches the vocabulary HQ already triages by.
 */

export const SUPPORT_STATUS_LABELS = {
  new: 'Open',
  open: 'Open',
  pending: 'Awaiting your reply',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const SUPPORT_STATUS_VARIANTS = {
  new: 'default',
  open: 'default',
  pending: 'secondary',
  resolved: 'outline',
  closed: 'outline',
};

export function supportTypeLabel(type) {
  if (type === 'idea') return 'Suggestion';
  if (type === 'support') return 'Support';
  return type || 'Support';
}

/**
 * Who a message is from, in the thread.
 *
 * Internal notes never reach this app — HQ writes them with no row permission, so they are
 * unreadable rather than filtered — but the mapping covers the value rather than relying on
 * that, so a change on the HQ side cannot turn one into a mystery blank name.
 */
export function messageAuthorLabel(message) {
  if (!message) return '';
  if (message.direction === 'outbound') return 'PanelGear Support';
  return message.fromName || message.fromEmail || 'You';
}

/** A date a person reads, not an ISO string. Undefined input renders as an em dash. */
export function formatSupportDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
